import { useState, useEffect, useRef, useMemo } from "react";
import { Check, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { cn } from "../../../lib/utils";
import { Input } from "../../ui/input";
import { useVirtualizer } from "@tanstack/react-virtual";
import { getIsError } from "../../../utils/getIsError";
import { useDispatch } from "react-redux";
import { setErrorFields } from "../../../store/tableSlice";
import NewCampaignModal from "./NewCampaignModal";

export const RichDropDownCell = ({
  data = { values: [], selected: undefined },
  onUpdate,
  originalRow,
  columnInfo,
  isEditable,
  setOptimisticValue,
  optimisticValue,
  isRequired,
  isEmpty,
  errorFields,
  rowId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);

  const isError = getIsError(errorFields, rowId, columnInfo.key);

  const selectedOption = useMemo(() => {
    // Handle the new value format where optimisticValue might be an object
    if (typeof optimisticValue === "object") {
      return optimisticValue; // Already in the correct format
    }
    // Fallback for cases where optimisticValue is just the glid
    return data.values.find((opt) => opt.glid === optimisticValue);
  }, [optimisticValue, data.values]);

  const filteredOptions = data.values.filter(
    (option) =>
      option?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      option?.glid?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSelect = async (glid) => {
    const selectedOpt = data.values.find((opt) => opt.glid === glid);

    if (!selectedOpt) {
      const existingError = errorFields.find((error) => error.rowId === rowId);

      if (existingError) {
        const updatedFields = Array.from(
          new Set([...existingError.fields, columnInfo.key])
        );
        dispatch(
          setErrorFields(
            errorFields.map((error) =>
              error.rowId === rowId
                ? { ...error, fields: updatedFields }
                : error
            )
          )
        );
      } else {
        dispatch(
          setErrorFields([
            ...errorFields,
            {
              rowId: rowId,
              fields: [columnInfo.key],
            },
          ])
        );
      }

      return;
    }

    // If selectedOpt is valid, remove the field from errorFields
    const updatedErrorFields = errorFields
      .map((error) => {
        if (error.rowId === rowId) {
          const filteredFields = error.fields.filter(
            (field) => field !== columnInfo.key
          );
          return { ...error, fields: filteredFields };
        }
        return error;
      })
      .filter((error) => error.fields.length > 0);

    dispatch(setErrorFields(updatedErrorFields));

    // Update optimisticValue with the full selected option object
    setOptimisticValue(selectedOpt);

    setIsOpen(false);
    setSearchQuery("");

    try {
      const result = await onUpdate?.(originalRow, {
        ...data,
        selected: selectedOpt.glid,
      });
      if (result?.error) {
        setOptimisticValue(data.selected);
      }
    } catch (error) {
      setOptimisticValue(data.selected);
    }
  };

  const scrollContainerRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 44,
    overscan: 5,
  });

  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsCalculating(true);
      const timer = setTimeout(() => {
        rowVirtualizer.measure();
        setIsCalculating(false);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isOpen, filteredOptions.length, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  const renderTriggerContent = () => {
    if (!selectedOption) {
      return (
        <div className="text-muted-foreground text-sm">
          {columnInfo.display}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {selectedOption.image && (
          <div className="flex-shrink-0">
            <img
              src={selectedOption.image}
              alt=""
              className="w-5 h-5 rounded-full object-cover"
            />
          </div>
        )}
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate">
            {selectedOption.title}
          </span>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>{selectedOption.description}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Popover open={isEditable ? isOpen : false} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              "min-h-[40px] w-full px-2 py-1 flex items-center rounded cursor-pointer group",
              isHovered && "bg-gray-50",
              isOpen && isEditable && "bg-gray-100",
              isError && "border border-red-500 bg-red-50"
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isRequired && isEmpty && (
              <span
                className="text-red-500 mr-1 align-middle"
                title="This is a required field."
              >
                *
              </span>
            )}
            {renderTriggerContent()}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-2">
          <TooltipProvider>
            <div className="space-y-2">
              {/* Search input */}
              <div className="flex items-center gap-2 px-2 py-1 border rounded-md focus-within:ring-1 focus-within:ring-ring">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search accounts..."
                  className="border-0 p-0 text-sm focus-visible:ring-0"
                />
              </div>
              {/* Only for campaign_id column: show New Campaign button */}
              {columnInfo.key === "campaign_id" && (
                <div className="flex justify-end mb-1">
                  <button
                    className="bg-primary text-primary-foreground rounded px-2 py-1 text-xs hover:bg-primary/90 transition"
                    type="button"
                    onClick={() => {
                      setShowNewCampaignModal(true);
                      setIsOpen(false);
                    }}
                  >
                    + New Campaign
                  </button>
                </div>
              )}
              {/* Virtualized Options list */}
              {filteredOptions.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-2">
                  No results found
                </div>
              ) : (
                <div
                  ref={scrollContainerRef}
                  className="max-h-[200px] overflow-y-auto"
                  style={{ position: "relative" }}
                >
                  <div
                    className={cn(
                      "transition-opacity duration-200",
                      isCalculating ? "opacity-0" : "opacity-100"
                    )}
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {!isCalculating &&
                      virtualRows.map((virtualRow) => {
                        const option = filteredOptions[virtualRow.index];
                        return (
                          <Tooltip key={option.glid} delayDuration={500}>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  `flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer ${
                                    !option.is_disable &&
                                    "hover:bg-accent hover:text-accent-foreground"
                                  } absolute w-full`,
                                  optimisticValue?.glid === option.glid &&
                                    "bg-accent text-accent-foreground",
                                  option.is_disable &&
                                    "opacity-50 cursor-not-allowed"
                                )}
                                style={{
                                  height: `${virtualRow.size}px`,
                                  transform: `translateY(${virtualRow.start}px)`,
                                }}
                                onClick={() => {
                                  if (option.is_disable) return;
                                  handleSelect(option.glid);
                                }}
                              >
                                {option.image && (
                                  <div className="flex-shrink-0">
                                    <img
                                      src={option.image}
                                      alt=""
                                      className="w-5 h-5 rounded-full object-cover"
                                    />
                                  </div>
                                )}
                                <div className="flex flex-col min-w-0 flex-1">
                                  <span className="text-sm font-medium truncate">
                                    {option.title}
                                  </span>
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <span>{option.description}</span>
                                  </div>
                                </div>
                                {optimisticValue?.glid === option.glid && (
                                  <Check className="h-4 w-4 flex-shrink-0" />
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <div className="space-y-1">
                                <div className="font-medium">
                                  {option.title}
                                </div>
                                {option.description && (
                                  <div className="text-xs opacity-75">
                                    {option.description}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </TooltipProvider>
        </PopoverContent>
      </Popover>
      {/* New Campaign Modal integration */}
      {columnInfo.key === "campaign_id" && (
        <NewCampaignModal
          adSetGlid={originalRow.glid.display}
          open={showNewCampaignModal}
          onClose={() => setShowNewCampaignModal(false)}
          adAccountId={originalRow.ad_account_id.selected?.glid}
          onSuccess={() => {
            // TODO: Refresh campaign list and select new campaign if needed
          }}
        />
      )}
    </>
  );
};
